"use client"

import { useState, useEffect, useRef } from "react"
import { AiOutlineCloudUpload } from "react-icons/ai"
import { useNavigate } from "react-router-dom"
// import { deleteFile } from "../../../../redux/features/applicationSlice"
import { updateApplicationNoLoader as updateApplicationNoLoaderUser } from "../../../../redux/features/applicationSlice"
import {
  deleteFile,
  updateApplicationNoLoader as updateApplicationNoLoaderAdmin,
} from "../../../../redux/features/adminLeadSlice"
import { useDispatch, useSelector } from "react-redux"
import DeleteModal from "../../../client/ExistApplication/DeleteModal"
import { FiCheckCircle } from "react-icons/fi"
import { motion, AnimatePresence } from "framer-motion"
import { RxCross2 } from "react-icons/rx"

export default function VisaTab({ selectedData, isAdmin = false }) {
  const FIELD_LIMITS = {
    institution: 100,
    institutionLocation: 100,
    program: 80,
    term: 40,
    documentName: 100,
  }

  const clampValue = (value, maxLength) => {
    if (typeof value !== "string") return value
    if (!maxLength) return value
    return value.slice(0, maxLength)
  }

  const sanitizeFieldValue = (field, value) => {
    if (typeof value !== "string") return value
    const cleanedValue = value.replace(/[\u0000-\u001F\u007F]/g, "")
    return clampValue(cleanedValue, FIELD_LIMITS[field])
  }

  const dispatch = useDispatch()
  const navigate = useNavigate()

  // States
  const [isUploading, setIsUploading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [formData, setFormData] = useState({
    customer: selectedData?.firstName + " " + selectedData?.lastName,
    visaDocName: selectedData?.visaDocName,
    applicationNo: selectedData.uniqueId,
    institution: selectedData.institution || "",
    institutionLocation: selectedData.institutionLocation || "",
    program: selectedData.program || "",
    term: selectedData.term || "",
    paymentReceived: selectedData.paymentStatus,
    visaStatus: selectedData.visaStatus || "",
    visaValidity: selectedData.visaValidity || "",
    reason: "",
  })

  const { applications } = useSelector((state) => state.application) || { applications: [] }
  const { leads } = useSelector((state) => state.leads) || { leads: [] }
  const applicationData = isAdmin ? leads : applications

  const [visaDocuments, setVisaDocuments] = useState([])
  const [documentName, setDocumentName] = useState(selectedData.visaDocName || "")
  const [selectedFile, setSelectedFile] = useState(null)
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    fileName: "",
    fileIndex: -1,
    fileId: "",
  })
  const [showSuccess, setShowSuccess] = useState(false)
  const [validationError, setValidationError] = useState("")
  const [uploadSuccess, setUploadSuccess] = useState("")
  const fileInputRef = useRef(null) // fixed from useState(null)
  const [formValidationError, setFormValidationError] = useState("")
  const isPaymentPaid = (selectedData?.paymentStatus || "").trim().toLowerCase() === "paid"

  // Choose the correct update function
  const updateApplicationNoLoader = isAdmin ? updateApplicationNoLoaderAdmin : updateApplicationNoLoaderUser

  // Load visa documents when applications change
  useEffect(() => {
    // Don't update if showing success modal (to prevent re-renders that trigger API calls)
    if (showSuccess) return
    
    const existingApplication = applicationData.find((application) => application._id === selectedData._id)
    if (existingApplication?.visa?.length) {
      setVisaDocuments(existingApplication.visa)
      // If there's already a file but no selected file, set the document name
      if (existingApplication.visa[0] && !selectedFile && !documentName) {
        setDocumentName(existingApplication.visa[0].docName || "")
      }
    }
  }, [applicationData, selectedData._id, selectedFile, documentName, showSuccess])

  // Auto-hide success message after 3 seconds
  useEffect(() => {
    let timer
    if (uploadSuccess) {
      timer = setTimeout(() => {
        setUploadSuccess("")
      }, 3000)
    }
    return () => clearTimeout(timer)
  }, [uploadSuccess])

  const handleSubmit = (e) => {
    e.preventDefault()
  }

  // motion variants (kept)
  const modalVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.3 } },
    exit: { opacity: 0, scale: 0.8, transition: { duration: 0.3 } },
  }

  const iconVariants = {
    hidden: { scale: 0, opacity: 0 },
    visible: { scale: 1, opacity: 1, transition: { delay: 0.2, duration: 0.3 } },
  }

  const messageVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
    exit: { opacity: 0, y: -10, transition: { duration: 0.3 } },
  }

  // Reusable upload logic extracted to function
  const uploadFile = async (file, { clearAfterUpload = true, showSuccessMessage = true, additionalFields = {} } = {}) => {
    try {
      setIsUploading(true)
      setValidationError("")

      const token = localStorage.getItem("token")
      if (!token) throw new Error("No authentication token found. Please log in.")

      const fd = new FormData()
      fd.append("visa", file)
      fd.append("docName", sanitizeFieldValue("documentName", documentName))
      Object.entries(additionalFields || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          fd.append(key, value)
        }
      })

      // optional logging for debugging
      // for (var pair of fd.entries()) {
      //   console.log(pair[0] + ":", pair[1])
      // }

      const result = await dispatch(
        updateApplicationNoLoader({
          id: selectedData._id,
          formData: fd,
          isFileUpload: true,
          section: "visa",
        }),
      )

      if (!result.error) {
        if (clearAfterUpload) {
          setSelectedFile(null)
          setDocumentName("")
        }
        setValidationError("")
        if (showSuccessMessage) {
          setUploadSuccess("Document uploaded successfully!")
        }

        if (clearAfterUpload && fileInputRef.current) {
          fileInputRef.current.value = ""
        }
        return true
      } else {
        setValidationError("Failed to upload document: " + (result.error.message || "Unknown error"))
        return false
      }
    } catch (err) {
      console.error(err)
      setValidationError("Failed to upload document. Please try again.")
      return false
    } finally {
      setIsUploading(false)
    }
  }

  // File change handler: validate size and keep the file for upload on Complete Application
  const handleFileChange = async (e) => {
    // Support both synthetic object calls and real file input events
    const file = e.target && e.target.files ? e.target.files[0] : null

    if (!file) return

    if (file.size > 5 * 1024 * 1024) {
      setValidationError("File size exceeds 5MB limit")
      if (e.target) e.target.value = ""
      return
    }

    setSelectedFile(file)
    setValidationError("")

    if (e.target) e.target.value = ""
  }

  // Update the handleSaveVisa function to use the new state
  const handleSaveVisa = async (finalize = false) => {
    try {
      // Validate all required fields if finalizing
      if (finalize) {
        // Check if payment has been made before finalizing
        if (!isPaymentPaid) {
          setFormValidationError("Payment must be Paid before finalizing the application")
          return
        }

        if (
          ((!selectedData.visa || selectedData.visa.length === 0) && !selectedFile) ||
          !formData.visaStatus ||
          (formData.visaStatus === "Approved" && !formData.visaValidity) || // <-- validation only for Approved
          (selectedData.visaCategory === "Study Visa" &&
            (!formData.program || !formData.term || !formData.institution || !formData.institutionLocation))
        ) {
          setFormValidationError("Please fill all the fields to complete the application")
          return
        }

        if (selectedFile && (!documentName || !documentName.trim())) {
          setFormValidationError("Please enter document name before completing the application")
          return
        }
      }

      setFormValidationError("") // Clear form validation error
      setIsSaving(true)

      const updatePayload = {
        visaStatus: formData.visaStatus,
        visaExpiryDate: formData.visaStatus === "Approved" ? formData.visaValidity : "",
        visaDocName: sanitizeFieldValue("documentName", documentName),
        institution: sanitizeFieldValue("institution", formData.institution),
        institutionLocation: sanitizeFieldValue("institutionLocation", formData.institutionLocation),
        program: sanitizeFieldValue("program", formData.program),
        term: sanitizeFieldValue("term", formData.term),
        status: finalize ? "Finalized" : selectedData.status,
        reason: finalize && (formData.visaStatus === "Approved" || formData.visaStatus === "Rejected") ? "Finalized the application" : "",
      }

      // Upload selected document first (if any), then finalize in one action.
      if (selectedFile) {
        const uploaded = await uploadFile(selectedFile, {
          clearAfterUpload: true,
          showSuccessMessage: false,
          additionalFields: updatePayload,
        })
        if (!uploaded) {
          return
        }
        if (finalize) {
          setShowSuccess(true)
        }
        return
      }

      const result = await dispatch(
        updateApplicationNoLoader({
          id: selectedData._id,
          formData: updatePayload,
        }),
      )

      // Check if the operation was successful
      if (!result.error) {
        if (finalize) {
          setShowSuccess(true)
        }
      } else {
        console.error("Error saving visa information:", result.error)
      }
    } catch (err) {
      console.error(err)
    } finally {
      setIsSaving(false)
    }
  }

  // Drag handlers
  const handleDragOver = (event) => {
    event.preventDefault()
  }

  const handleDrop = (event) => {
    event.preventDefault()
    if (event.dataTransfer.files.length > 0) {
      // call handleFileChange with synthetic event shape
      handleFileChange({ target: { files: event.dataTransfer.files } })
    }
  }

  const removeFile = () => {
    setSelectedFile(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: sanitizeFieldValue(name, value),
    }))
  }

  // Handle delete document click
  const handleDeleteClick = (fileName, index, fileId) => {
    setDeleteModal({
      isOpen: true,
      fileName,
      fileIndex: index,
      fileId,
    })
  }

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    try {
      await dispatch(
        deleteFile({
          applicationId: selectedData._id,
          category: "visa",
          fileId: deleteModal.fileId,
        }),
      )
      setDeleteModal({
        isOpen: false,
        fileName: "",
        fileIndex: -1,
        fileId: "",
      })
    } catch (err) {
      console.error(err)
    }
    removeFile()
  }

  return (
    <div className="bg-[#FFFBF5] border border-[#CAC3BB] p-3 md:p-6 text-sm md:text-base">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Invoice Details <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.customer}
                readOnly
                className="w-full p-2.5 bg-white border border-gray-200 rounded-md text-gray-900"
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Visa Type <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.applicationNo}
                readOnly
                className="w-full p-2.5 bg-white border border-gray-200 rounded-md text-gray-900"
              />
            </div>

            {selectedData.visaCategory === "Study Visa" && (
              <>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Institute Name <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <input
                      type="text"
                      name="institution"
                      value={formData.institution}
                      onChange={handleChange}
                      className="w-full p-2.5 bg-white border border-gray-200 rounded-md text-gray-900 pr-10"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Institute Location <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <input
                      type="text"
                      name="institutionLocation"
                      value={formData.institutionLocation}
                      onChange={handleChange}
                      className="w-full p-2.5 bg-white border border-gray-200 rounded-md text-gray-900 pr-10"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Program <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <input
                      type="text"
                      name="program"
                      value={formData.program || ""}
                      onChange={handleChange}
                      className="w-full p-2.5 bg-white border border-gray-200 rounded-md text-gray-900 pr-10"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Term <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <input
                      type="text"
                      name="term"
                      value={formData.term || ""}
                      onChange={handleChange}
                      className="w-full p-2.5 bg-white border border-gray-200 rounded-md text-gray-900 pr-10"
                    />
                  </div>
                </div>
              </>
            )}

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Visa Status  <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.visaStatus}
                onChange={(e) => setFormData((prev) => ({ ...prev, visaStatus: e.target.value }))}
                className="w-full p-2.5 bg-white border border-gray-200 rounded-md text-gray-900"
              >
                <option value="">Select status</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Visa Expiry Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="visaValidity"
                value={formData.visaValidity}
                onChange={handleChange}
                disabled={formData.visaStatus === "Rejected"}   // <-- disable logic
                className={`w-full p-2.5 border rounded-md text-gray-900 ${formData.visaStatus === "Rejected"
                  ? "bg-gray-200 cursor-not-allowed"
                  : "bg-white"
                  }`}
              />
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Enter Name of Document  <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="e.g Visa Grant Letter"
                value={documentName}
                onChange={(e) =>
                  setDocumentName(sanitizeFieldValue("documentName", e.target.value))
                }
                className="w-full p-2.5 bg-white border border-gray-200 rounded-md text-gray-900"
              />
            </div>

            <div className="space-y-4 bg-[#fdfaf4]">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Upload Document <span className="text-red-500">*</span>
                </label>

                {selectedFile ? (
                  <div className="border border-gray-200 rounded-md p-4 bg-white">
                    <div className="flex items-center justify-between">
                      <span className="text-blue-500 ">{selectedFile.name}</span>
                      <button type="button" onClick={removeFile} className="text-red-500 hover:text-red-700">
                        <RxCross2 className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    className="border border-gray-200 rounded-md p-6 text-center bg-white cursor-pointer relative"
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                  >
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      className="hidden"
                      id="fileInput"
                      ref={fileInputRef}
                      onChange={handleFileChange}
                    />
                    <label htmlFor="fileInput" className="block cursor-pointer">
                      <AiOutlineCloudUpload className="mx-auto text-gray-500 text-2xl" />
                      <p className="text-gray-800">Click to upload or drag and drop</p>
                      <p className="text-xs text-gray-600">Word Document or PDF (Max. 5MB)</p>
                    </label>
                  </div>
                )}
                <AnimatePresence>
                  {validationError && (
                    <motion.div
                      variants={messageVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      className="text-red-500 text-sm font-medium"
                    >
                      {validationError}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Document List */}
            <div className="space-y-2 mt-4">
              <label className="block text-sm font-medium text-gray-700">Uploaded Documents</label>
              <div className="space-y-2">
                {selectedData.visa &&
                  selectedData.visa.map((doc, index) => (
                    <div key={doc._id} className="flex items-center justify-between">
                      <a
                        href={doc.location}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:underline "
                      >
                        {doc.name}.{doc.format === "application/pdf" ? "pdf" : doc.format.split("/")[1]}
                      </a>
                      <button
                        type="button"
                        onClick={() => handleDeleteClick(doc.name, index, doc._id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <RxCross2 className="h-5 w-5" />
                      </button>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-end space-y-2">
          {formValidationError && (
            <motion.div
              variants={messageVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="text-red-500 text-sm font-medium"
            >
              {formValidationError}
            </motion.div>
          )}
          {uploadSuccess && (
            <motion.div
              variants={messageVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="text-green-500 text-sm font-medium"
            >
              {uploadSuccess}
            </motion.div>
          )}
          <div className="flex items-end justify-end">
            <button
              type="button"
              onClick={() => (selectedData.status !== "Finalized" ? handleSaveVisa(true) : handleSaveVisa(false))}
              disabled={isSaving || isUploading}
              className={`w-max p-2 px-12 rounded-full transition flex items-center justify-center ${
                isSaving || isUploading
                  ? "bg-gray-400 text-gray-600 cursor-not-allowed"
                  : "bg-[#005787] text-white hover:bg-[#005a7d]"
              }`}
              title={selectedData.status !== "Finalized" && !isPaymentPaid ? "Payment must be Paid before finalizing" : ""}
            >
              {isSaving || isUploading ? (
                <>
                  <span className="loader-small"></span>
                  <span className="ml-2">{isUploading ? "Uploading..." : "Saving..."}</span>
                </>
              ) : selectedData.status !== "Finalized" && (
                "Complete Application"
              )}
            </button>
          </div>
        </div>
      </form>

      {showSuccess && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50">
          <motion.div
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="absolute bg-[#FFFBF5] border border-[#CAC3BB] rounded-lg shadow-lg p-8 w-full max-w-md text-center"
          >
            <motion.div
              variants={iconVariants}
              initial="hidden"
              animate="visible"
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <FiCheckCircle className="h-16 w-16 text-[#036494]" />
            </motion.div>

            <h2 className="text-xl font-semibold mb-6 text-[#1E1E1E]">This application is completed successfully</h2>

            <button
              type="button"
              onClick={(e) => {
                e.preventDefault()
                const path = window.location.pathname
                if (path.includes("agent")) {
                  navigate("/agent/leads")
                } else if (path.includes("admin")) {
                  navigate("/admin/leads")
                } else {
                  navigate("/")
                }
              }}
              className="w-full bg-[#036494] text-white py-2 rounded-full hover:bg-[#075985] transition-colors"
            >
              Back to application
            </button>
          </motion.div>
        </div>
      )}
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={() =>
          setDeleteModal({
            isOpen: false,
            fileName: "",
            fileIndex: -1,
            fileId: "",
          })
        }
        onConfirm={handleDeleteConfirm}
        fileName={deleteModal.fileName}
        sectionName="Visa Document"
      />
    </div>
  )
}
